import React from 'react';
import { Plus, Heart, TrendingUp, TrendingDown, ArrowRightLeft, Trash2 } from 'lucide-react';
import Card, { CardHeader, CardTitle } from '../ui/Card.tsx';
import Button from '../ui/Button.tsx';

export type ActivityType = 'added' | 'removed' | 'wanted' | 'unwanted' | 'price_up' | 'price_down' | 'trade';

interface Activity {
  id: string;
  type: ActivityType;
  cardId: string;
  cardName: string;
  setName: string;
  imageUrl?: string;
  price?: number;
  priceChange?: number;
  timestamp: Date;
}

interface RecentActivityFeedProps {
  activities: Activity[];
  loading?: boolean;
  onCardClick?: (cardId: string) => void;
}

const activityConfig: Record<ActivityType, {
  icon: typeof Plus;
  label: string;
  color: string;
  bgColor: string;
}> = {
  added: {
    icon: Plus,
    label: 'Added to collection',
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
  },
  removed: {
    icon: Trash2,
    label: 'Removed from collection',
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
  },
  wanted: {
    icon: Heart,
    label: 'Added to wishlist',
    color: 'text-pink-400',
    bgColor: 'bg-pink-500/20',
  },
  unwanted: {
    icon: Heart,
    label: 'Removed from wishlist',
    color: 'text-gray-400',
    bgColor: 'bg-gray-500/20',
  },
  price_up: {
    icon: TrendingUp,
    label: 'Price increased',
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
  },
  price_down: {
    icon: TrendingDown,
    label: 'Price decreased',
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
  },
  trade: {
    icon: ArrowRightLeft,
    label: 'Marked for trade',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
  },
};

export default function RecentActivityFeed({
  activities,
  loading = false,
  onCardClick
}: RecentActivityFeedProps) {
  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  if (loading) {
    return (
      <Card variant="elevated" padding="lg">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-700 rounded w-36 mb-4" />
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="flex gap-3">
                <div className="w-8 h-8 bg-gray-700 rounded-full" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-700 rounded w-40 mb-2" />
                  <div className="h-3 bg-gray-700 rounded w-24" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card variant="elevated" padding="lg">
      <CardHeader>
        <CardTitle>Recent Activity</CardTitle>
      </CardHeader>

      {activities.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500">No recent activity</p>
          <p className="text-sm text-gray-600 mt-1">
            Add cards to your collection to see activity here
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {activities.slice(0, 10).map(activity => {
            const config = activityConfig[activity.type];
            const Icon = config.icon;

            return (
              <button
                key={activity.id}
                onClick={() => onCardClick?.(activity.cardId)}
                className="flex items-start gap-3 w-full text-left group"
              >
                <div className={`p-2 rounded-full ${config.bgColor} flex-shrink-0`}>
                  <Icon className={`w-4 h-4 ${config.color}`} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-white group-hover:text-blue-400 transition-colors truncate">
                      {activity.cardName}
                    </span>
                    {activity.price !== undefined && (
                      <span className={`text-sm font-medium ${
                        activity.type === 'price_up' ? 'text-green-400' :
                        activity.type === 'price_down' ? 'text-red-400' :
                        'text-gray-400'
                      }`}>
                        {formatPrice(activity.price)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{activity.setName}</span>
                    <span>&middot;</span>
                    <span>{config.label}</span>
                    <span>&middot;</span>
                    <span>{formatTime(activity.timestamp)}</span>
                  </div>
                  {activity.priceChange !== undefined && activity.priceChange !== 0 && (
                    <div className={`text-xs mt-1 ${
                      activity.priceChange > 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {activity.priceChange > 0 ? '+' : ''}{formatPrice(activity.priceChange)}
                      {' '}
                      ({activity.priceChange > 0 ? '+' : ''}{((activity.priceChange / (activity.price! - activity.priceChange)) * 100).toFixed(1)}%)
                    </div>
                  )}
                </div>

                {activity.imageUrl && (
                  <div className="w-8 h-11 bg-gray-800 rounded overflow-hidden flex-shrink-0">
                    <img
                      src={activity.imageUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {activities.length > 10 && (
        <Button variant="ghost" size="sm" className="w-full mt-4">
          View all activity
        </Button>
      )}
    </Card>
  );
}
